import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import {
  ArrowLeft, RefreshCw, LogOut, Clock, CheckCircle,
  XCircle, AlertTriangle, FileSearch, ChevronDown, ChevronUp,
} from 'lucide-react';

const COR = {
  laranja:  '#F07020',
  azul:     '#1E6DB5',
  ciano:    '#29B6D8',
  verde:    '#22C55E',
  amarelo:  '#F5A623',
  vermelho: '#EF4444',
  bg:       '#0A0F1A',
  card:     '#0E1826',
  card2:    '#121f30',
  borda:    '#1A2740',
  texto:    '#F1F5F9',
  sub:      '#64748B',
};

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return `${dt.getDate().toString().padStart(2,'0')}/${(dt.getMonth()+1).toString().padStart(2,'0')}/${dt.getFullYear()} ${dt.getHours().toString().padStart(2,'0')}:${dt.getMinutes().toString().padStart(2,'0')}`;
};

const STATUS_CONFIG = {
  pendente: {
    label:  'Aguardando análise',
    cor:    '#F5A623',
    bg:     '#F5A62315',
    borda:  '#F5A62344',
    icone:  <Clock size={13}/>,
  },
  resolvido_novo: {
    label:  'Aprovado — registro inserido',
    cor:    '#22C55E',
    bg:     '#22C55E15',
    borda:  '#22C55E44',
    icone:  <CheckCircle size={13}/>,
  },
  resolvido_existente: {
    label:  'Não aprovado — registro existente mantido',
    cor:    '#EF4444',
    bg:     '#EF444415',
    borda:  '#EF444444',
    icone:  <XCircle size={13}/>,
  },
};

function getStatusKey(dup) {
  if (dup.status === 'pendente') return 'pendente';
  if (dup.decisao === 'novo') return 'resolvido_novo';
  return 'resolvido_existente';
}

export default function MinhasDuplicidades() {
  const { usuario, logout } = useAuth();
  const navigate = useNavigate();

  const [duplicidades, setDuplicidades] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [filtro, setFiltro]             = useState('todos');  // todos | pendente | resolvido
  const [expandido, setExpandido]       = useState(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/registros/duplicidades/minhas');
      setDuplicidades(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const lista = duplicidades.filter(d => {
    if (filtro === 'todos')     return true;
    if (filtro === 'pendente')  return d.status === 'pendente';
    if (filtro === 'resolvido') return d.status === 'resolvido';
    return true;
  });

  const totalPendente  = duplicidades.filter(d => d.status === 'pendente').length;
  const totalAprovado  = duplicidades.filter(d => d.status === 'resolvido' && d.decisao === 'novo').length;
  const totalRejeitado = duplicidades.filter(d => d.status === 'resolvido' && d.decisao === 'existente').length;

  const handleLogout = () => { logout(); navigate('/login'); };

  return (
    <div style={{ minHeight: '100vh', background: COR.bg, padding: '20px 24px', fontFamily: 'sans-serif' }}>
      {/* barra gradiente topo */}
      <div style={{ position:'fixed', top:0, left:0, right:0, height:3, background:`linear-gradient(90deg,${COR.laranja},${COR.ciano},${COR.azul})`, zIndex:100 }}/>

      {/* HEADER */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24, paddingTop:8 }}>
        <div style={{ display:'flex', alignItems:'center', gap:16 }}>
          <button
            style={{ background:COR.card, border:`1px solid ${COR.borda}`, borderRadius:8, padding:'8px 12px', color:COR.sub, cursor:'pointer', display:'flex', alignItems:'center', gap:6, fontSize:12 }}
            onClick={() => navigate('/dashboard')}>
            <ArrowLeft size={14}/> Voltar
          </button>
          <div style={{ width:1, height:40, background:COR.borda }}/>
          <div>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <FileSearch size={16} color={COR.ciano}/>
              <h1 style={{ color:COR.texto, fontSize:20, fontWeight:700, margin:0 }}>Minhas Duplicidades</h1>
            </div>
            <p style={{ color:COR.sub, fontSize:11, marginTop:2 }}>
              Acompanhe o status das solicitações que você enviou ao supervisor
            </p>
          </div>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <button
            style={{ background:COR.card, border:`1px solid ${COR.borda}`, borderRadius:8, padding:'8px 10px', color:COR.sub, cursor:'pointer' }}
            onClick={carregar}>
            <RefreshCw size={14}/>
          </button>
          <button
            style={{ display:'flex', alignItems:'center', gap:6, background:COR.card, border:'1px solid #ef444455', borderRadius:8, padding:'8px 14px', color:'#ef4444', cursor:'pointer', fontSize:12 }}
            onClick={handleLogout}>
            <LogOut size={14}/> Sair
          </button>
        </div>
      </div>

      {/* CARDS DE RESUMO */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:14, marginBottom:24 }}>
        <CardResumo
          titulo="Aguardando análise"
          valor={totalPendente}
          cor={COR.amarelo}
          icone={<Clock size={14} color={COR.amarelo}/>}
        />
        <CardResumo
          titulo="Aprovados"
          valor={totalAprovado}
          cor={COR.verde}
          icone={<CheckCircle size={14} color={COR.verde}/>}
        />
        <CardResumo
          titulo="Não aprovados"
          valor={totalRejeitado}
          cor={COR.vermelho}
          icone={<XCircle size={14} color={COR.vermelho}/>}
        />
      </div>

      {/* FILTROS */}
      <div style={{ display:'flex', gap:6, marginBottom:20 }}>
        {[
          { key: 'todos',     label: `Todos (${duplicidades.length})` },
          { key: 'pendente',  label: `Pendentes (${totalPendente})` },
          { key: 'resolvido', label: `Resolvidos (${totalAprovado + totalRejeitado})` },
        ].map(({ key, label }) => (
          <button key={key}
            style={{
              background: filtro === key ? `linear-gradient(135deg,${COR.laranja},#C85A10)` : COR.card,
              color:  filtro === key ? '#fff' : COR.sub,
              border: `1px solid ${filtro === key ? COR.laranja : COR.borda}`,
              borderRadius: 8, padding: '7px 20px', cursor: 'pointer', fontSize: 13, fontWeight: 500,
            }}
            onClick={() => setFiltro(key)}>
            {label}
          </button>
        ))}
      </div>

      {/* CONTEÚDO */}
      {loading ? (
        <div style={{ display:'flex', justifyContent:'center', padding:'60px 0' }}>
          <div style={{ width:32, height:32, border:`3px solid ${COR.borda}`, borderTop:`3px solid ${COR.laranja}`, borderRadius:'50%', animation:'spin 1s linear infinite' }}/>
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        </div>
      ) : lista.length === 0 ? (
        <div style={{ textAlign:'center', padding:'60px 0', color:COR.sub }}>
          <FileSearch size={48} color={COR.borda} style={{ marginBottom:12 }}/>
          <p style={{ fontSize:16, fontWeight:600, color:COR.texto, margin:'0 0 6px 0' }}>
            {filtro === 'todos' ? 'Nenhuma duplicidade enviada ainda.' : 'Nenhum registro neste filtro.'}
          </p>
          <p style={{ fontSize:12, margin:0 }}>
            Quando você enviar um CNPJ duplicado ao supervisor, ele aparecerá aqui.
          </p>
        </div>
      ) : (
        <>
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            {lista.map(dup => {
              const sk  = getStatusKey(dup);
              const cfg = STATUS_CONFIG[sk];
              const isExp = expandido === dup.id;

              return (
                <div key={dup.id}
                  style={{ background:COR.card, border:`1px solid ${cfg.borda}`, borderRadius:12, overflow:'hidden' }}>

                  {/* Cabeçalho do card */}
                  <div
                    style={{ padding:'14px 18px', display:'flex', alignItems:'center', justifyContent:'space-between', cursor:'pointer', borderBottom: isExp ? `1px solid ${COR.borda}` : 'none' }}
                    onClick={() => setExpandido(isExp ? null : dup.id)}>

                    <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                      {/* dot de status */}
                      <div style={{ width:8, height:8, borderRadius:'50%', background:cfg.cor, boxShadow:`0 0 8px ${cfg.cor}`, flexShrink:0 }}/>

                      <div>
                        <p style={{ color:COR.texto, fontSize:13, fontWeight:700, margin:0 }}>
                          {dup.razao_social || '—'}
                        </p>
                        <p style={{ color:COR.sub, fontSize:11, margin:'2px 0 0 0' }}>
                          CNPJ: {dup.cnpj} · Enviado em: {fmtDate(dup.criado_em)}
                        </p>
                      </div>
                    </div>

                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      {/* tipo badge */}
                      <span style={{
                        background: dup.tipo === 'indicacao' ? `${COR.amarelo}22` : `${COR.verde}22`,
                        color:  dup.tipo === 'indicacao' ? COR.amarelo : COR.verde,
                        border: `1px solid ${dup.tipo === 'indicacao' ? `${COR.amarelo}44` : `${COR.verde}44`}`,
                        borderRadius:6, padding:'3px 10px', fontSize:11, fontWeight:700,
                      }}>
                        {dup.tipo.toUpperCase()}
                      </span>

                      {/* status badge */}
                      <span style={{
                        display:'flex', alignItems:'center', gap:4,
                        background: cfg.bg, color: cfg.cor,
                        border: `1px solid ${cfg.borda}`,
                        borderRadius:6, padding:'3px 10px', fontSize:11, fontWeight:700,
                      }}>
                        {cfg.icone} {cfg.label}
                      </span>

                      <span style={{ color:COR.sub, fontSize:16 }}>
                        {isExp ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
                      </span>
                    </div>
                  </div>

                  {/* Detalhes expandidos */}
                  {isExp && (
                    <div style={{ padding:'18px' }}>
                      <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:16, marginBottom: dup.status === 'resolvido' ? 16 : 0 }}>
                        {/* Dados do registro */}
                        <div>
                          <p style={{ color:COR.ciano, fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:0.5, margin:'0 0 10px 0' }}>
                            📋 Dados enviados
                          </p>
                          <div style={{ background:'#080f1a', border:`1px solid ${COR.ciano}33`, borderRadius:10, padding:'12px 14px' }}>
                            <Campo label="Razão Social"    valor={dup.razao_social}/>
                            <Campo label="CNPJ"            valor={dup.cnpj}/>
                            <Campo label="Cidade / Estado" valor={`${dup.cidade || '—'} / ${dup.estado || '—'}`}/>
                            <Campo label="Contato"         valor={dup.nome_completo}/>
                            <Campo label="Telefone"        valor={dup.telefone}/>
                            <Campo label="E-mail"          valor={dup.email}/>
                            <Campo label="Ramo"            valor={dup.ramo_atividade}/>
                            <Campo label="Origem"          valor={dup.origem}/>
                          </div>
                        </div>

                        {/* Linha do tempo / decisão */}
                        <div>
                          <p style={{ color:COR.amarelo, fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:0.5, margin:'0 0 10px 0' }}>
                            🕐 Linha do tempo
                          </p>
                          <div style={{ background:'#080f1a', border:`1px solid ${COR.amarelo}33`, borderRadius:10, padding:'12px 14px' }}>
                            <TimelineItem
                              cor={COR.ciano}
                              titulo="Solicitação enviada"
                              detalhe={fmtDate(dup.criado_em)}
                              done
                            />
                            {dup.status === 'pendente' ? (
                              <TimelineItem
                                cor={COR.amarelo}
                                titulo="Aguardando análise do supervisor"
                                detalhe="Em fila de análise"
                                pulsing
                              />
                            ) : (
                              <TimelineItem
                                cor={dup.decisao === 'novo' ? COR.verde : COR.vermelho}
                                titulo={dup.decisao === 'novo' ? 'Novo registro aprovado ✓' : 'Registro existente mantido ✗'}
                                detalhe={`${dup.resolvido_por || '—'} · ${fmtDate(dup.resolvido_em)}`}
                                done
                              />
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Banner de resultado para resolvidos */}
                      {dup.status === 'resolvido' && (
                        <div style={{
                          display:'flex', alignItems:'center', gap:10,
                          background: cfg.bg, border:`1px solid ${cfg.borda}`,
                          borderRadius:10, padding:'12px 16px',
                        }}>
                          <span style={{ color:cfg.cor, fontSize:20 }}>
                            {dup.decisao === 'novo' ? '✓' : '✗'}
                          </span>
                          <div>
                            <p style={{ color:cfg.cor, fontSize:13, fontWeight:700, margin:0 }}>
                              {dup.decisao === 'novo'
                                ? 'Seu registro foi aprovado e inserido no sistema!'
                                : 'O supervisor manteve o registro existente. Seu envio não foi inserido.'}
                            </p>
                            <p style={{ color:COR.sub, fontSize:11, margin:'2px 0 0 0' }}>
                              Resolvido por {dup.resolvido_por || '—'} em {fmtDate(dup.resolvido_em)}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ── Sub-componentes ──────────────────────────────────────────────────────────

function CardResumo({ titulo, valor, cor, icone }) {
  return (
    <div style={{
      background: `linear-gradient(135deg,${cor}18,${cor}06)`,
      border: `1px solid ${cor}40`,
      borderRadius:12, padding:'12px 16px',
    }}>
      <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:4 }}>
        {icone}
        <span style={{ color:cor, fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:0.5 }}>
          {titulo}
        </span>
      </div>
      <span style={{ color:cor, fontSize:32, fontWeight:800 }}>{valor}</span>
    </div>
  );
}

function Campo({ label, valor }) {
  return (
    <div style={{ marginBottom:6 }}>
      <span style={{ color:COR.sub, fontSize:10, display:'block' }}>{label}</span>
      <span style={{ color:valor ? COR.texto : '#334155', fontSize:12 }}>{valor || '—'}</span>
    </div>
  );
}

function TimelineItem({ cor, titulo, detalhe, done, pulsing }) {
  return (
    <div style={{ display:'flex', gap:10, marginBottom:14, alignItems:'flex-start' }}>
      <div style={{ flexShrink:0, marginTop:2 }}>
        <div style={{
          width:10, height:10, borderRadius:'50%',
          background: done || pulsing ? cor : COR.borda,
          boxShadow: pulsing ? `0 0 0 4px ${cor}33` : done ? `0 0 8px ${cor}` : 'none',
          animation: pulsing ? 'pulse 1.5s ease-in-out infinite' : 'none',
        }}/>
      </div>
      <div>
        <p style={{ color: done || pulsing ? COR.texto : COR.sub, fontSize:12, fontWeight:600, margin:0 }}>{titulo}</p>
        <p style={{ color:COR.sub, fontSize:11, margin:'2px 0 0 0' }}>{detalhe}</p>
      </div>
      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}}`}</style>
    </div>
  );
}
